import React, { useEffect, useState, useCallback } from 'react';
import { useFormik } from 'formik';
import axios from 'axios';
import { logAddressConfirmation } from '../../../api/requests';
import StyledSelect from '@shared/components/Select';
import Input from '@shared/components/Input2';
import Textarea from '@shared/components/Textarea';
import cityData from '@data/cityData';
import { object, string, number, boolean } from 'yup';
import { useSecondModal } from '@helpers/hooks/useSecondModal';
import {useSetRecoilState, useRecoilValue, useRecoilState} from "recoil";
import { 
  selectedShippingAddress, 
  aboutUs,
  imagesData,
  loadedImagesCount,
  imageFrameState,
  clientCroppedImagesState 
} from '@atoms';
import './style.css';

const BASE_URL = process.env.REACT_APP_BASE_URL;
const PHONE_NUMBER_REGEX = /^0[2-9]\d{7,8}$/;

const validationSchema = object().shape({
  fullName: string().required('Required'),
  phoneNumber: string()
    .required('Required')
    .matches(PHONE_NUMBER_REGEX, 'Invalid phone number')
    .test('phone-number', 'Invalid phone number', (value) => {
      if (!value) {
        return true;
      }

      const prefix = value.substring(0, 2);
      const length = value.length;

      if ((prefix === '05' || prefix === '07') && length === 10) {
        return true;
      }

      if (
        (prefix === '03' || prefix === '04' || prefix === '09' || prefix === '02') &&
        length === 9
      ) {
        return true;
      }

      return false;
    }),
  email: string().required('Required'),
  address: string().required('Required'),
  addressDetails: string().required('Required'),
  city: string().required('Required'),
  arrivalInstructions: string(),
  checkBox: boolean().oneOf([true], 'You must accept the terms and conditions'),
});

const AddAddressModalMobile = () => {
  const modal = useSecondModal();
  const setAddress = useSetRecoilState(selectedShippingAddress);
  const selectedAddress = useRecoilValue(selectedShippingAddress);
  const [isLoading, setLoading] = useState(false);
  const [cityPlaceholder, setCityPlaceholder] = useState(true);
  const [selectInputValue, setSelectInputValue] = useState('');
  const [, setAboutUs] = useRecoilState(aboutUs);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [hasPrefilled, setHasPrefilled] = useState(false);
  
  // Get image data from Recoil state
  const images = useRecoilValue(imagesData);
  const imageCount = useRecoilValue(loadedImagesCount);
  const frameType = useRecoilValue(imageFrameState) || 'unknown';
  const clientCroppedImages = useRecoilValue(clientCroppedImagesState);
  
  // Create a function to determine if a field should show an error
  const shouldShowError = useCallback((fieldName) => {
    // Always validate prefilled fields (email and fullName) if they exist
    if (hasPrefilled && (fieldName === 'email' || fieldName === 'fullName')) {
      return true;
    }
    
    // For other fields, only show errors if the form has been submitted or the field has been touched
    return formSubmitted || (formik.touched[fieldName] && formik.errors[fieldName]);
  }, [formSubmitted, hasPrefilled]);

  const formik = useFormik({
    initialValues: {
      fullName: '',
      phoneNumber: '',
      email: '',
      address: '',
      addressDetails: '',
      zipCode: '', // Keeping this field in initialValues for backward compatibility
      city: '',
      arrivalInstructions: '',
      checkBox: true, // Initialize checkbox as checked
    },
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: false,
    validationSchema,
    onSubmit: async (values) => {
      console.log('AddAddressMobile2 - Form onSubmit triggered, checkbox state in formik:', values.checkBox);
      console.log('AddAddressMobile2 - Current selectedAddress before update:', selectedAddress);
      setFormSubmitted(true);
      
      // Validate all fields before submission
      await formik.validateForm();
      console.log('AddAddressMobile2 - Form validation complete, current errors:', formik.errors);
      
      // Explicitly check the checkbox value using Formik state
      if (!values.checkBox) {
        console.log('AddAddressMobile2 - Checkbox validation failed in onSubmit');
        formik.setFieldError('checkBox', 'You must accept the terms and conditions');
        return;
      }
      
      console.log('AddAddressMobile2 - All validations passed, proceeding with form submission');
      // Check if there are any validation errors
      if (Object.keys(formik.errors).length === 0) {
        setLoading(true);
        console.log('AddAddressMobile2 - Setting address in Recoil state:', values);
        setAddress(values);
        
        // We can't immediately log the updated selectedAddress here because Recoil state updates are asynchronous
        // Instead, we'll add a small timeout to check the state after it's had time to update
        setTimeout(() => {
          console.log('AddAddressMobile2 - Checking selectedAddress after update (async):', selectedAddress);
        }, 100);
        
        console.log('AddAddressMobile2 - Form submitted successfully with values:', values);
        setLoading(false);
        console.log('AddAddressMobile2 - Closing modal');
        handleCloseModal();
        
        // Add another timeout to check if the selectedAddress is still available after the modal is closed
        setTimeout(() => {
          console.log('AddAddressMobile2 - Checking selectedAddress after modal close (async):', selectedAddress);
        }, 200);
      } else {
        console.log('AddAddressMobile2 - Form has validation errors:', formik.errors);
      }
    },
  });

  // Add a useEffect to fetch cart data
  useEffect(() => {
    const fetchCartData = async () => {
      try {
        const sessionId = localStorage.getItem("sessionId");
        if (sessionId) {
          const uid = localStorage.getItem("uniqueUserId") || "";
          const userEmail = localStorage.getItem("userEmail") || "";
          const logIdentifier = userEmail ? `[${uid}-${userEmail}]` : `[${uid}]`;
          console.log(`${logIdentifier} Fetching cart data for mobile address form`);
          
          const response = await axios.get(`${BASE_URL}/api/addtocart/session/${sessionId}`);
          
          if (response.data.success && response.data.cart.userInfo) {
            const userInfo = response.data.cart.userInfo;
            
            // Update the form values with the cart data
            formik.setValues({
              ...formik.values,
              fullName: userInfo.fullName || formik.values.fullName,
              email: userInfo.email || formik.values.email,
              phoneNumber: userInfo.phoneNumber || formik.values.phoneNumber,
              address: userInfo.address || formik.values.address,
              addressDetails: userInfo.addressDetails || formik.values.addressDetails,
              city: userInfo.city || formik.values.city,
              zipCode: userInfo.zipCode || formik.values.zipCode,
              arrivalInstructions: userInfo.arrivalInstructions || formik.values.arrivalInstructions,
              checkBox: true
            });
            
            // Only pre-fill the form fields above — do NOT set selectedShippingAddress here.
            // The Recoil state should only be set when the user explicitly submits the form.

            // Set hasPrefilled to true if we have prefilled any fields
            if (userInfo.fullName || userInfo.email) {
              setHasPrefilled(true);
            }
            
            console.log(`${logIdentifier} Successfully pre-filled mobile address form with cart data`);
          }
        }
      } catch (error) {
        console.error("Error fetching cart data for mobile address form:", error);
      }
    };
    
    fetchCartData();
  }, []);
  
  useEffect(() => {
    // Log when the modal is opened
    console.log('Mobile Terms Address Modal opened');
    console.log('Initial checkbox state:', formik.values.checkBox);

    if (selectedAddress) {
      // Preserve checkBox as true when loading address data
      // The checkBox field may be undefined if loaded from cart userInfo
      formik.setValues({
        ...selectedAddress,
        checkBox: selectedAddress.checkBox !== false // Default to true unless explicitly false
      });

      // Log after setting values from selectedAddress
      console.log('Values set from selectedAddress, checkbox state:', formik.values.checkBox);

      // Check if email and fullName are prefilled
      if (selectedAddress.email || selectedAddress.fullName) {
        setHasPrefilled(true);

        // Validate only the prefilled fields
        if (selectedAddress.email) {
          formik.validateField('email');
        }

        if (selectedAddress.fullName) {
          formik.validateField('fullName');
        }
      }
    }
  }, []);

  const handleAboutUsModal = (type) => {
    setAboutUs(type)
    modal('open', 'aboutUs');
  }
  
  // Custom submit handler to ensure checkbox validation
  const handleSubmit = (e) => {
    console.log('AddAddressMobile2 - handleSubmit triggered');
    console.log('AddAddressMobile2 - Current form values:', formik.values);
    console.log('AddAddressMobile2 - Current selectedAddress from Recoil:', selectedAddress);
    
    if (!formik.values.checkBox) {
      console.log('AddAddressMobile2 - Checkbox validation failed');
      e.preventDefault();
      setFormSubmitted(true);
      formik.setFieldError('checkBox', 'You must accept the terms and conditions');
      return false;
    }
    console.log('AddAddressMobile2 - Checkbox validation passed, proceeding with form submission');
    return formik.handleSubmit(e);
  };
  
  const handleCloseModal = () => {
    console.log('AddAddressMobile2 - handleCloseModal called');
    console.log('AddAddressMobile2 - Current selectedAddress before closing modal:', selectedAddress);
    modal('close', 'addAddressMobile');
    console.log('AddAddressMobile2 - Modal close action triggered');
    
    // Add a timeout to check if the selectedAddress is still available after the modal is closed
    setTimeout(() => {
      console.log('AddAddressMobile2 - Checking selectedAddress after modal close (async):', selectedAddress);
    }, 200);
  };

  return (
    <>
      <form className="add-address-modal-mobile" onSubmit={(e) => {
        console.log('Form onSubmit handler triggered, checkbox state in formik:', formik.values.checkBox);
        
        // Use Formik state for validation
        if (!formik.values.checkBox) {
          console.log('Form submission prevented: Terms not accepted');
          e.preventDefault();
          setFormSubmitted(true);
          formik.setFieldError('checkBox', 'You must accept the terms and conditions');
          return false;
        }
        
        console.log('Form onSubmit validation passed, proceeding with handleSubmit');
        return formik.handleSubmit(e);
      }}>
        <div className="add-address-modal-mobile__header">
          <span onClick={() => handleCloseModal()}>&gt;</span>
          <span>נזין את פרטי הכתובת</span>
        </div>
        <div className="add-address-modal-mobile__content rtl">
          <div className="form__row">
            <Input
              label="שם פרטי ושם משפחה"
              name="fullName"
              placeholder="שם פרטי ושם משפחה"
              onChange={(e) => {
                formik.handleChange(e);
                // If the field was cleared, update the Formik state
                if (e.target.value === '') {
                  formik.setFieldValue(e.target.name, '');
                }
              }}
              value={formik.values.fullName}
              error={shouldShowError('fullName') ? formik.errors.fullName : undefined}
              isRequired={true}
              icon={
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M9 17C13.4183 17 17 13.4183 17 9C17 4.58172 13.4183 1 9 1C4.58172 1 1 4.58172 1 9C1 13.4183 4.58172 17 9 17Z"
                    stroke="#727272"
                    strokeWidth="1.5"
                    strokeMiterlimit="10"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M14.3333 14.8667C14.3333 11.9211 11.9456 9.53334 8.99996 9.53334C6.05436 9.53334 3.66663 11.9211 3.66663 14.8667"
                    stroke="#727272"
                    strokeWidth="1.5"
                    strokeMiterlimit="10"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M9.00005 9.53333C10.7674 9.53333 12.2 8.10064 12.2 6.33333C12.2 4.56602 10.7674 3.13333 9.00005 3.13333C7.23274 3.13333 5.80005 4.56602 5.80005 6.33333C5.80005 8.10064 7.23274 9.53333 9.00005 9.53333Z"
                    stroke="#727272"
                    strokeWidth="1.5"
                    strokeMiterlimit="10"
                    strokeLinejoin="round"
                  />
                </svg>
              }
            />
          </div>
          <div className="form__row">
            <Input
              name="email"
              type="email"
              label="אימייל"
              placeholder="אימייל"
              value={formik.values.email}
              onChange={(e) => {
                formik.handleChange(e);
                // If the field was cleared, update the Formik state
                if (e.target.value === '') {
                  formik.setFieldValue(e.target.name, '');
                }
              }}
              error={shouldShowError('email') ? formik.errors.email : undefined}
              isRequired={true}
              icon={
                <svg width="16" height="12" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M15.3846 3.69231C15.2214 3.69231 15.0649 3.75714 14.9495 3.87255C14.8341 3.98796 14.7692 4.14448 14.7692 4.30769V9.23077C14.7692 9.39398 14.7044 9.5505 14.589 9.66591C14.4736 9.78132 14.3171 9.84615 14.1538 9.84615H1.84615C1.68294 9.84615 1.52642 9.78132 1.41101 9.66591C1.2956 9.5505 1.23077 9.39398 1.23077 9.23077V4.30769C1.23077 4.14448 1.16593 3.98796 1.05053 3.87255C0.93512 3.75714 0.778595 3.69231 0.615385 3.69231C0.452174 3.69231 0.295649 3.75714 0.180242 3.87255C0.064835 3.98796 0 4.14448 0 4.30769V9.23077C0.000430897 9.72023 0.195219 10.1895 0.541539 10.5354C0.887427 10.8817 1.35669 11.0765 1.84615 11.0769H14.1538C14.6433 11.0765 15.1126 10.8817 15.4585 10.5354C15.8048 10.1895 15.9996 9.72023 16 9.23077V4.30769C16 4.14448 15.9352 3.98796 15.8198 3.87255C15.7044 3.75714 15.5478 3.69231 15.3846 3.69231Z"
                    fill="#727272"
                  />
                  
                  <path
                    d="M7.63085 7.26154C7.73737 7.34143 7.86693 7.38461 8.00008 7.38461C8.13323 7.38461 8.26279 7.34143 8.36931 7.26154L15.637 1.80923C15.7504 1.72373 15.8307 1.60166 15.8644 1.46371C15.898 1.32575 15.8829 1.1804 15.8216 1.05231C15.73 0.862967 15.6072 0.69032 15.4585 0.541538C15.1127 0.195219 14.6434 0.000430897 14.1539 0H1.84624C1.35677 0.000430897 0.88751 0.195219 0.541621 0.541538C0.392917 0.69032 0.270192 0.862967 0.178544 1.05231C0.117262 1.1804 0.102165 1.32575 0.135813 1.46371C0.16946 1.60166 0.249782 1.72373 0.36316 1.80923L7.63085 7.26154ZM1.84624 1.23077H14.1539C14.211 1.22178 14.2692 1.22178 14.3262 1.23077L8.00008 6L1.67393 1.23077C1.73101 1.22178 1.78915 1.22178 1.84624 1.23077Z"
                    fill="#727272"
                  />
                </svg>
              }
            />
          </div>
          <div className="form__row">
            <Input
              name="phoneNumber"
              type="tel"
              label="מספר טלפון"
              placeholder="מספר טלפון"
              value={formik.values.phoneNumber}
              onChange={e => {
                if (e.target.value > 9999999999) {
                  e.target.value = Math.floor(e.target.value / 10);
                  return;
                }
                formik.handleChange(e);
                // If the field was cleared, update the Formik state
                if (e.target.value === '') {
                  formik.setFieldValue(e.target.name, '');
                }
              }}
              onBlur={formik.handleBlur}
              error={shouldShowError('phoneNumber') ? formik.errors.phoneNumber : undefined}
              isRequired={true}
              icon={
                <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M10.216 4.29206L13.7025 0.806553L13.h291 0.398814C12.7592 -0.132938 11.8301 -0.132938 11.2983 0.398814L9.80827 1.88885C9.54146 2.15566 9.3949 2.50985 9.3949 2.88565C9.3949 3.26144 9.54146 3.61563 9.80639 3.88151L10.216 4.29206Z"
                    fill="#727272"
                  />
                  <path
                    d="M3.89513 9.7937C3.36338 9.26194 2.43422 9.26194 1.90247 9.7937L0.412436 11.2837C0.14656 11.5505 0 11.9047 0 12.2805C0 12.6563 0.146561 13.0105 0.414315 13.2783L0.824873 13.6841L4.30381 10.2052L3.89513 9.7937Z"
                    fill="#727272"
                  />
                  <path
                    d="M14.9727 2.08047L14.6401 1.74789L11.1564 5.23153L11.7671 5.84126C11.8564 5.93051 11.9043 6.04795 11.9043 6.17384C11.9043 6.29973 11.8554 6.41716 11.7671 6.50642L6.51912 11.7544C6.34156 11.931 6.03246 11.932 5.8549 11.7544L5.24423 11.1437L1.76624 14.6293L2.09882 14.9618C2.34308 15.2061 3.26942 16 4.9699 16C6.48812 16 8.96837 15.3311 12.157 12.1434C18.5202 5.77831 15.1239 2.23173 14.9727 2.08047Z"
                    fill="#727272"
                  />
                </svg>
              }
              maxValue={9999999999}
              maxLength={10}
            />
          </div>
          <div className="form__row">
            <Input
              name="address"
              label="רחוב ומספר בית או תא דואר"
              placeholder="רחוב ומספר בית או תא דואר"
              value={formik.values.address}
              onChange={(e) => {
                formik.handleChange(e);
                // If the field was cleared, update the Formik state
                if (e.target.value === '') {
                  formik.setFieldValue(e.target.name, '');
                }
              }}
              error={shouldShowError('address') ? formik.errors.address : undefined}
              isRequired={true}
              icon={
                <svg width="19" height="18" viewBox="0 0 19 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M16.3851 17H2.84647V9.61531H1.76031C1.05999 9.61531 0.73322 8.74207 1.26061 8.28114L8.4656 1.45831C9.11053 0.847229 10.1204 0.847229 10.7653 1.45831L17.9697 8.28114C18.4977 8.74146 18.1709 9.61531 17.47 9.61531H16.3851V17Z"
                    stroke="#727272"
                    strokeWidth="1.5"
                    strokeMiterlimit="10"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12.0773 17H7.15417V13.3077C7.15417 11.9483 8.25634 10.8461 9.61574 10.8461C10.9751 10.8461 12.0773 11.9483 12.0773 13.3077V17Z"
                    stroke="#727272"
                    strokeWidth="1.5"
                    strokeMiterlimit="10"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              }
            />
            <Input
              name="addressDetails"
              label="דירה, כניסה, קומה"
              placeholder="דירה, כניסה, קומה"
              onChange={(e) => {
                formik.handleChange(e);
                // If the field was cleared, update the Formik state
                if (e.target.value === '') {
                  formik.setFieldValue(e.target.name, '');
                }
              }}
              value={formik.values.addressDetails}
              error={shouldShowError('addressDetails') ? formik.errors.addressDetails : undefined}
              isRequired={true}
            />
          </div>
          <div className="form__row column">
            <div style={{ width: '100%' }}>
              <StyledSelect
                name="city"
                placeholder="יישוב"
                search
                options={cityData}
                onChange={i => formik.setFieldValue('city', i.value)}
                value={cityData.find(itm => itm.value === formik.values.city)}
                defaultValue={cityData.find(itm => itm.value === formik.values.city)}
                error={shouldShowError('city') ? formik.errors.city : undefined}
                isRequired={true}
                menuPlacement="top"
                inputValue={selectInputValue}
                onFocus={e => {
                  setSelectInputValue(formik.values.city);
                  setCityPlaceholder(false);
                }}
                onInputChange={setSelectInputValue}
              />
            </div>
          </div>
          <div className="form__row">
            <Textarea
              // label="הוראות הגעה (אופציונלי)"
              placeholder="הערות, העדפות, קוד לבניין ועוד הוראות לשליח"
              value={formik.values.arrivalInstructions}
              onChange={formik.handleChange}
              name="arrivalInstructions"
              style={{ minHeight: 0 }}
              rows="1"
            />
          </div>
          <div className="form_row">
            <button type="submit" className="submit" onClick={async (e) => {
              // Log the entire formik instance to debug
              console.log('Formik instance:', formik);
              console.log('Formik values:', formik.values);
              console.log('Submit button clicked, checkbox state in formik:', formik.values.checkBox);
              
              // Use Formik state for validation
              if (!formik.values.checkBox) {
                console.log('Validation failed: Terms not accepted');
                e.preventDefault();
                setFormSubmitted(true);
                formik.setFieldError('checkBox', 'You must accept the terms and conditions');
                return false;
              }
              
              // Log address confirmation to server
              const logAddressConfirmationToServer = async () => {
                try {
                  // Get image data from Recoil state
                  const imageUrls = images.map(img => img.crop_image || img.url);
                  const cropDataArray = images.map(img => img.cropbox_data);
                  
                  // Use the actual image count from Recoil state
                  const actualImageCount = images.length || imageCount;
                  
                  console.log(`Logging address confirmation with ${actualImageCount} images and frame type: ${frameType}`);
                  
                  // Use the imported logAddressConfirmation function
                  await logAddressConfirmation(
                    formik.values,
                    'MOBILE',
                    imageUrls,
                    cropDataArray,
                    actualImageCount,
                    frameType
                  );
                  
                  // Add user to Flashy list 28236
                  if (window.flashy && typeof window.flashy.contacts.createOrUpdate === 'function') {
                    const names = formik.values.fullName.trim().split(' ');
                    const firstName = names[0];
                    const lastName = names.length > 1 ? names.slice(1).join(' ') : '';
                    const sessionId = localStorage.getItem("sessionId");
                    const uid = localStorage.getItem("uniqueUserId") || "";
                    const userEmail = formik.values.email || "";
                    const logIdentifier = userEmail ? `[${uid}-${userEmail}]` : `[${uid}]`;
                    
                    window.flashy.contacts.createOrUpdate({
                      "email": formik.values.email,
                      "first_name": firstName,
                      "last_name": lastName,
                      "phone": formik.values.phoneNumber,
                      "sessionid": sessionId,
                      "updated_at": new Date().toISOString(),
                      "lists": {
                        28236: true
                      }
                    });
                    
                    console.log(`${logIdentifier} Added user to Flashy list 28236: ${formik.values.email}, sessionId: ${sessionId}`);
                  }
                } catch (error) {
                  console.error('Error logging address confirmation:', error);
                }
              };
              
              // Call the logging function
              logAddressConfirmationToServer();
              
              console.log('Checkbox validation passed, proceeding with form submission');
                setLoading(true); // Show loading spinner
                
                
                setTimeout(() => {
                setLoading(false); // Hide loading spinner after 1s
                return formik.handleSubmit(e);
                }, 1000);
              }}>
                 {isLoading ? (
                   <div className="spinner-container">
                     <div className="spinner"></div>
                   </div>
                 ) : 'הוספת כתובת'}
              </button>
              <div className={`mobile-terms-address-modal${(formSubmitted && formik.errors.checkBox) ? "-checkbox-error" : ""}`}>
                <input className="mobile-address-checkbox"
                   type="checkbox"
                   id="checkBox"
                   name="checkBox"
                   checked={formik.values.checkBox} // Use Formik state for checked prop
                     onChange={() => {
                       const newValue = !formik.values.checkBox; // Toggle based on Formik state
                       console.log('Checkbox changed, new state:', newValue);
                       formik.setFieldValue("checkBox", newValue); // Update Formik state
                     }}
              />
              <span>קראתי ואני מאשר\ת את &nbsp;
                <span onClick={() => handleAboutUsModal('PP')} style={{textDecoration: "underline"}}>
                מדיניות הפרטיות
                </span>&nbsp;
                ואת &nbsp;  
                <span onClick={() => handleAboutUsModal('SR')} style={{textDecoration: "underline"}}>
                תנאי השימוש
                </span>&nbsp;
                המפורטים באתר
              </span>
            </div>
          </div>
        </div>
      </form>
      {isLoading && (
        <div
        className="modal loaderbg"
        id="mainImageLoaderModal"
        tabIndex="-1"
        role="dialog"
        aria-labelledby="exampleModalLabel"
        aria-hidden="true"
        style={{ display: isLoading }}
      >
        <div className="modal-dialog review-image-loader" role="document">
          <div className="loadingio-spinner-heart-btbrqan8295">
            <div className="ldio-kv0ui0pfesk">
              <div>
                <div></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )}
  </>
);
};

export default AddAddressModalMobile;
